import { queryOptions, useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { SQLImportRow } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { requireWorkspaceId } from '../shared/workspace';
import {
  parseSqlImportsResponse,
  SQL_IMPORT_DATABASES,
  sqlImportKeys,
  sqlImportRowKeys,
  type SqlImport,
  type SqlImportDatabase,
} from './entity';

const STALE_TIME = 0;

/**
 * @public SQL imports for one database. The list endpoint requires the
 * `?database=<type>` query and sideloads the databases, so the response is read
 * through a local cast and joined by `parseSqlImportsResponse`. A failed read
 * falls back to an empty array (per-database `hashSettled` semantics — one
 * database failing must not blank the others).
 *
 * `staleTime: 0` — read non-cached; the list drives the directory SQL-import gate.
 */
export async function getSqlImportsByDatabase(
  workspaceId: number,
  database: SqlImportDatabase,
  qc: QueryClient,
): Promise<SqlImport[]> {
  try {
    const raw = await hydraUiApi.sqlImports.list({
      params: { path: { environment_id: String(workspaceId) }, query: { database } },
    } as unknown as Parameters<typeof hydraUiApi.sqlImports.list>[0]);
    const parsed = parseSqlImportsResponse(raw);
    for (const s of parsed) {
      qc.setQueryData(sqlImportKeys.detail(workspaceId, s.id), s);
    }
    return parsed;
  } catch {
    return [];
  }
}

/** @public */
export const getSqlImportsByDatabaseQueryOptions = (
  workspaceId: number,
  database: SqlImportDatabase,
  qc: QueryClient,
) =>
  queryOptions({
    queryKey: sqlImportKeys.list(workspaceId, database),
    queryFn: () => getSqlImportsByDatabase(workspaceId, database, qc),
    staleTime: STALE_TIME,
  });

export type SqlImportsByDatabase = Record<SqlImportDatabase, SqlImport[]>;

/**
 * @public The six per-database SQL-import reads, combined into a record keyed by
 * database. Mirrors the Ember `hashSettled` over the six databases: each query
 * settles independently and contributes an empty array while pending or on
 * error. `isPending` is true until every database has settled.
 *
 * Pass an explicit `workspaceId` when the caller has already resolved it (e.g. a
 * page that resolved the `/workspaces/last` sentinel from the router location):
 * `requireWorkspaceId` reads `window.location`, which lags the router location
 * during that redirect, so a caller mid-transition must supply the id it resolved
 * rather than let this hook re-read the racing URL. Omit it to resolve from the URL.
 */
export function useSqlImportsByDatabase(workspaceId?: number): {
  byDatabase: SqlImportsByDatabase;
  isPending: boolean;
} {
  const resolvedWorkspaceId = workspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const results = useQueries({
    queries: SQL_IMPORT_DATABASES.map((database) =>
      getSqlImportsByDatabaseQueryOptions(resolvedWorkspaceId, database, qc),
    ),
  });

  const byDatabase = {} as SqlImportsByDatabase;
  SQL_IMPORT_DATABASES.forEach((database, index) => {
    byDatabase[database] = results[index]?.data ?? [];
  });

  return { byDatabase, isPending: results.some((result) => result.isPending) };
}

/**
 * @public The import rows for one SQL import (the connections overview derives
 * per-sync failure status from these). Backed by
 * `GET /sql_import_rows?sql_import_id=N` — the list-imports endpoint does not
 * sideload rows. Errors resolve to `[]`.
 *
 * The helper lives under `sqlImports` rather than `sqlImportRows` because the
 * route is tagged `sql_imports` in the spec (services#24475).
 */
export async function getSqlImportRows(workspaceId: number, importId: string): Promise<SQLImportRow[]> {
  try {
    const data = await hydraUiApi.sqlImports.getSqlImportRows({
      params: { path: { environment_id: String(workspaceId) }, query: { sql_import_id: importId } },
    });
    return data.sql_import_rows;
  } catch {
    return [];
  }
}

/** @public */
export const getSqlImportRowsQueryOptions = (workspaceId: number, importId: string) =>
  queryOptions({
    queryKey: sqlImportRowKeys.list(workspaceId, importId),
    queryFn: () => getSqlImportRows(workspaceId, importId),
    staleTime: STALE_TIME,
  });
